export type ApiErrorKind =
  | 'offline'
  | 'timeout'
  | 'session_expired'
  | 'forbidden'
  | 'conflict'
  | 'provider_unavailable'
  | 'request_failed'

export interface ApiErrorPayload {
  code: string
  message: string
  correlation_id: string
  retryable: boolean
  safe_details: Record<string, unknown>
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function kindForStatus(status: number): ApiErrorKind {
  if (status === 401) return 'session_expired'
  if (status === 403) return 'forbidden'
  if (status === 408) return 'timeout'
  if (status === 409) return 'conflict'
  if ([502, 503, 504].includes(status)) return 'provider_unavailable'
  return 'request_failed'
}

export class ApiClientError extends Error implements ApiErrorPayload {
  readonly name = 'ApiClientError'

  constructor(
    message: string,
    public readonly status: number,
    public readonly code: string,
    public readonly correlation_id: string,
    public readonly retryable: boolean,
    public readonly safe_details: Record<string, unknown>,
    public readonly kind: ApiErrorKind,
  ) {
    super(message)
  }
}

export function normalizeHttpError(
  payload: unknown,
  status: number,
  responseCorrelationId = '',
): ApiClientError {
  const body = isRecord(payload) ? payload : {}
  const detail = isRecord(body.detail) ? body.detail : {}
  const message = typeof body.message === 'string'
    ? body.message
    : typeof detail.message === 'string'
      ? detail.message
      : typeof body.detail === 'string'
        ? body.detail
        : 'Не удалось выполнить действие.'
  const code = typeof body.code === 'string'
    ? body.code
    : typeof detail.code === 'string'
      ? detail.code
      : `HTTP_${status}`
  const correlationId = typeof body.correlation_id === 'string'
    ? body.correlation_id
    : responseCorrelationId
  const safeDetails = isRecord(body.safe_details) ? body.safe_details : {}
  const retryable = typeof body.retryable === 'boolean'
    ? body.retryable
    : status === 408 || status === 429 || status >= 500
  return new ApiClientError(
    message,
    status,
    code,
    correlationId,
    retryable,
    safeDetails,
    kindForStatus(status),
  )
}

export function offlineError(): ApiClientError {
  return new ApiClientError(
    'Нет соединения с интернетом.',
    0,
    'OFFLINE',
    '',
    true,
    {},
    'offline',
  )
}

export function timeoutError(): ApiClientError {
  return new ApiClientError(
    'Сервер отвечает слишком долго.',
    408,
    'TIMEOUT',
    '',
    true,
    {},
    'timeout',
  )
}
